import React, { useState, useRef, useEffect } from 'react'
import { IconButton, InputAdornment, TextField, Popover, List, Typography, CircularProgress, Box, useMediaQuery, useTheme } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { makeStyles } from 'tss-react/mui'
import { useShop } from '../../hooks'
import { SearchResponseModel } from '../../models'
import SearchCard from './SearchCard'
import { getImageUrlBySize } from '../../utils'
import { ShopImageThumnbailSizes, ShopProductPageTypes } from '../../types'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from '@e3dc-react/shell/Hooks/useDebounce'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

const useStyles = makeStyles()(theme => ({
  transition: {
    border: 'none',
    transition: 'width 0.3s ease-in-out',
    width: '48px',
    '.MuiOutlinedInput-root': {
      border: 'none',
    },
    '.MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiInputBase-input': {
      textAlign: 'center',
    },
    '& .MuiInputLabel-root': {
      textAlign: 'center',
    },
  },
  expanded: {
    width: '350px',
    '.MuiOutlinedInput-root': {
      backgroundColor: theme.palette.shades.shade04,
    },
  },
  contentAlign: {
    minWidth: '350px',
    textAlign: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  searchIcon: {
    transform: 'translateY(4px)',
  },
  searchContainer: {
    position: 'fixed',
    left: 0,
    right: 0,
    top: '100px',
    display: 'flex',
    justifyContent: 'center',
    zIndex: 1,
  },
}))

const SEARCH_DEBOUNCE_TIME = 500
const MIN_INPUT_LENGTH = 3

/*
 * This component shows the SearchBar on the Header for Shop Products.
 */
const Search: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useCustomTranslation()
  const { classes, cx } = useStyles()
  const theme = useTheme()
  const mdDown = useMediaQuery(theme.breakpoints.down('md'))
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [expanded, setExpanded] = useState<boolean>(false)
  const [isSearchActive, setIsSearchActive] = useState<boolean>(mdDown ? false : true)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [searchData, setSearchData] = useState<SearchResponseModel | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const inputRef = useRef<HTMLInputElement>(null)
  const hasProductsAvailable = Boolean(searchData?.elements && searchData?.elements.length > 0)
  const { getSearchSuggestions } = useShop()
  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_TIME)

  const handleOutsideClick = (event: MouseEvent): void => {
    if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
      if (searchTerm.length === 0) {
        setExpanded(false)
      }
      if (mdDown && isSearchActive) {
        setIsSearchActive(false)
      }
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick)

    return () => {
      document.removeEventListener('click', handleOutsideClick)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, expanded, isSearchActive])

  useEffect(() => {
    if (debouncedSearchTerm.length >= MIN_INPUT_LENGTH) {
      setSearchData(null)
      setIsLoading(true)
      getSearchSuggestions(debouncedSearchTerm)
        .then(result => {
          setSearchData(result)
        })
        .catch(error => {
          console.error(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm])

  const handleSearchClick = (): void => {
    setExpanded(true)
    if (hasProductsAvailable && expanded) {
      getAllProductsList()
    } else {
      setAnchorEl(null)
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(event.target.value)

    if (event.target.value.length >= MIN_INPUT_LENGTH) {
      setAnchorEl(inputRef.current)
    } else {
      setAnchorEl(null)
    }
  }

  const handleInputBlur = (): void => {
    if (searchTerm.length === 0) {
      setExpanded(false)
    }
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const onProductClick = (productId: string): void => {
    navigate(`/shop/product/${productId}`)
    setSearchTerm('')
    handleClose()
  }

  const getAllProductsList = (): void => {
    navigate(`/shop/category/${ShopProductPageTypes.Search}/${searchTerm}`)
    setSearchTerm('')
    handleClose()
  }

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' && hasProductsAvailable && debouncedSearchTerm.length >= MIN_INPUT_LENGTH) {
      getAllProductsList()
      handleClose()
    }
  }

  const handleInputFocus = (): void => {
    if (searchTerm.length >= MIN_INPUT_LENGTH) {
      setAnchorEl(inputRef.current)
    }
  }

  const handleMobileButtonClick = (event: React.MouseEvent): void => {
    event.stopPropagation()
    setExpanded(true)
    setIsSearchActive(true)
  }

  return (
    <Box>
      {mdDown && <SearchIcon className={classes.searchIcon} onClick={e => handleMobileButtonClick(e)} />}
      {isSearchActive && (
        <Box className={cx({ [classes.searchContainer]: mdDown })}>
          <TextField
            ref={inputRef}
            className={cx(classes.transition, { [classes.expanded]: expanded })}
            value={searchTerm}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyPress={handleKeyPress}
            onFocus={handleInputFocus}
            placeholder={t('shop.search.search-placeholder')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <IconButton onClick={handleSearchClick}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            autoFocus
            autoComplete="off"
          />
          {expanded && (
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleClose}
              disableAutoFocus
              disableScrollLock
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{ sx: { maxHeight: '50vh' } }}
            >
              {searchTerm.length >= MIN_INPUT_LENGTH && (
                <List>
                  {!isLoading ? (
                    <>
                      {hasProductsAvailable ? (
                        <>
                          {searchData?.elements.map(product => {
                            const image = getImageUrlBySize(product.cover?.media?.thumbnails, ShopImageThumnbailSizes.Small)
                            return (
                              <SearchCard
                                key={product.id}
                                productId={product.id}
                                image={image}
                                title={product.translated.name}
                                productNumber={product.productNumber}
                                handleProductClick={onProductClick}
                              />
                            )
                          })}
                        </>
                      ) : (
                        <Typography className={classes.contentAlign} variant="body2">
                          {t('shop.search.no-products-available')}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Box className={classes.contentAlign}>
                      <CircularProgress size={20} />
                    </Box>
                  )}
                </List>
              )}
            </Popover>
          )}
        </Box>
      )}
    </Box>
  )
}

export default Search
